import Loader from 'components/ui/LoadingIndicator';
import { Avatar, Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import { MessageView } from 'components/ui/Feedback';
import { Contributor, Channel } from '../../../shared/types';
import { CONTRIBUTOR_PAGE } from '../../../gql/contributors';
import ArticlePreviewList from '../../ui/ArticlePreviewList';
import SectionHeader from '../../ui/SectionHeader';
import { getInitials } from '../../../utils/string.utils';
import { useActivityLogger } from '../../hooks/use-activity-logger';
import { useSecureFileUrl } from '../../hooks/use-secure-file-url';
import ChannelPreviewList from '../../ui/ChannelPreviewList';
import { sanitizeRouteId } from '../../../utils/route-params.utils';

import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { Grid } from '@mui/material';

interface ContributorDetailsProps {
    channels?: Channel[];
    contributor: Contributor;
    imgLink?: string;
}

const ContributorDetails = ({
    contributor,
    channels,
    imgLink,
}: ContributorDetailsProps) => (
    <Box
        sx={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            textAlign: { xs: 'center', sm: 'left' },
        }}
    >
        <Box
            id="contributor-details-wrapper"
            sx={{
                display: 'flex',
                height: '100%',
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', sm: 'flex-start' },
            }}
        >
            <Avatar
                sx={{
                    bgcolor: 'primary.main',
                    mr: { xs: 0, sm: 4 },
                    mb: { xs: 4, sm: 0 },
                    fontWeight: 'fontWeightRegular',
                }}
                src={imgLink}
            >
                {getInitials(contributor.name)}
            </Avatar>
            <Box id="contributor-details">
                <Typography
                    sx={{
                        textAlign: { xs: 'center', sm: 'left' },
                        mb: channels?.length ? 1 : 0,
                    }}
                    variant="h3"
                >
                    {contributor.name}
                </Typography>
            </Box>
        </Box>
    </Box>
);

const ContributorPage = () => {
    const { push } = useHistory();
    const params = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(params._id);
    const hasContributorId = !!_id;
    const [isArticleSectionVisible, setIsArticleSectionVisible] =
        useState(false);

    const errorMessage = (
        <MessageView
            styleName="primary"
            size="large"
            title="Contributor not found"
            primaryAction={{
                label: 'Return to Neon Insights',
                onClick: () => push('/'),
            }}
        />
    );

    const { data, loading } = useQuery<{ document: Contributor }>(
        CONTRIBUTOR_PAGE,
        {
            skip: !hasContributorId,
            fetchPolicy: 'network-only',
            variables: { id: _id },
        }
    );

    useActivityLogger('Contributor', data?.document?._id, data?.document?._id);
    const imgLink = useSecureFileUrl(
        hasContributorId
            ? '/contributor-profile-picture/' +
                  window.encodeURIComponent(_id as string)
            : undefined
    );

    if (!hasContributorId) {
        return errorMessage;
    }

    if (loading) {
        return <Loader />;
    }

    if (!data?.document) {
        return errorMessage;
    }

    const { document: contributor } = data;
    const channels = contributor.channels || [];

    return (
        <Grid container spacing={8}>
            <Grid size={12}>
                <ContributorDetails
                    contributor={contributor}
                    channels={channels}
                    imgLink={imgLink}
                />
            </Grid>

            {!!channels.length && (
                <Grid size={12}>
                    <Box sx={{ mb: 4 }}>
                        <SectionHeader
                            id="contributor-contributions-section"
                            title={`Channels curated by ${contributor.name}`}
                        />
                        <Box sx={{ mt: 2 }}>
                            <ChannelPreviewList
                                channels={channels}
                                loading={loading}
                            />
                        </Box>
                    </Box>
                </Grid>
            )}
            <Grid size={12}>
                <Box
                    sx={{ display: isArticleSectionVisible ? 'block' : 'none' }}
                >
                    <SectionHeader
                        id="contributor-article-section"
                        title={`Articles ${contributor.name} has contributed to`}
                        subtitle="All the article contributions by this person"
                    />
                    <ArticlePreviewList
                        contributorId={_id}
                        source="Contributor Page"
                        onDataLoaded={(numArticles) =>
                            setIsArticleSectionVisible(!!numArticles)
                        }
                    />
                </Box>
            </Grid>
        </Grid>
    );
};

export default ContributorPage;
